// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AssistantAttachments.test.tsx
import { Text } from "@radix-ui/themes";
import { type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { openPrivateAsset } from "../../../services/files";

/** Image with loading ghost - shows skeleton until image loads, reserves space */
export function ImageWithLoader({
  src,
  alt,
  href,
  testId = "chat-image-attachment-link",
}: {
  src: string;
  alt: string;
  href: string;
  /** Per-file id where several surfaces show images side by side. */
  testId?: string;
}) {
  const { t } = useTranslation("ui");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  // why: the new tab has no service worker to add the token, so a private href would open as 403.
  const openSigned = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    void openPrivateAsset(href);
  };

  return (
    <div className="relative block min-h-32 min-w-32 overflow-hidden rounded-xl shadow-sm ring-1 ring-puntt-neutral-gray-5">
      {!loaded && !error && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          aria-hidden
        >
          <div className="h-32 w-48 animate-pulse rounded-lg bg-puntt-neutral-gray-11" />
        </div>
      )}
      {error ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={openSigned}
          data-testid={`${testId}-error`}
          aria-label={t("chatMessage.openInNewTab", { alt })}
          className="flex h-24 items-center justify-center rounded-lg border border-puntt-neutral-gray-10 bg-puntt-neutral-gray-11/50 px-4 text-puntt-neutral-gray-11"
        >
          <Text size="2">{t("imageFailedToLoadClickToOpen")}</Text>
        </a>
      ) : (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={openSigned}
          data-testid={testId}
          aria-label={t("chatMessage.openInNewTab", { alt })}
          className="block"
        >
          <img
            src={src}
            alt={alt}
            className={`max-h-48 max-w-full object-contain transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
        </a>
      )}
    </div>
  );
}
